import { jUserLittleSchema } from './user';
import { filtersSchema, formatListSchema, ordersSchema } from './utils';
import { rightsSchema } from './common';
import { z } from 'zod';
import { jObjectBaseSchema, jObjectLittleSchema } from './objectBase';

export const createGroupSchema = z.object({
    type: z.string(),
    title: z.string(),
    description: z.string(),
    privacy: z.union([z.string(), z.number()]),
    edito: z.string().nullable(),
    language: z.string(),
    category: z.string(),
    idCommunity: z.string().optional(),
});
export type CreateGroup = z.infer<typeof createGroupSchema>;

export const spotBannerImageSchema = z.object({
    id: z.string(),
    name: z.string(),
    size: z.string(),
});
export type SpotBannerImage = z.infer<typeof spotBannerImageSchema>;

export const groupUserLevelSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type GroupUserLevel = z.infer<typeof groupUserLevelSchema>;

export const groupMemberLevelSchema = z.object({
    level: groupUserLevelSchema,
    user: jUserLittleSchema,
});
export type GroupMemberLevel = z.infer<typeof groupMemberLevelSchema>;

export const userLevelSpotSchema = z.object({
    RF_Constant_levelName: z.string(),
    RF_delete: z.boolean(),
    RF_editing: z.boolean(),
    RF_level: z.number(),
    RF_levelName: z.string(),
    RF_levelName_Trad: z.string(),
    RF_parameters: z.boolean(),
    RF_setAdmin: z.boolean(),
    RF_spotit: z.boolean(),
    RF_spotit_own: z.boolean(),
});
export type UserLevelSpot = z.infer<typeof userLevelSpotSchema>;

export enum GroupModerateStatus {
    SPOT_MODERATE_IGNORE = 0,
    SPOT_MODERATE_ACCEPT = 1,
    SPOT_MODERATE_PENDING = 2,
}
export const groupModerateStatusSchema = z.nativeEnum(GroupModerateStatus);
export enum GroupPrivacy {
    SPOT_PRIVATE = 0,
    SPOT_PUBLIC = 1,
    SPOT_MODERATE = 2,
}
export const groupPrivacy = z.nativeEnum(GroupPrivacy);

export const groupMemberSchema = z.object({
    userLevelSpot: userLevelSpotSchema,
    spotMinLevelToInvit: z.number(),
    userRights: rightsSchema,
    accessRequestStatus: groupModerateStatusSchema.optional(),
    groupPrivacy: groupPrivacy,
});
export type UserLevelAndRight = z.infer<typeof groupMemberSchema>;

export const valueLabelSchema = z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
});
export type ValueLabel = z.infer<typeof valueLabelSchema>;

export const groupWidgetLittleSchema = z.object({
    id: z.string(),
    title: z.string(),
    enabled: z.boolean().optional(),
});
export type GroupWidgetLittle = z.infer<typeof groupWidgetLittleSchema>;

export const groupCustomWidgetSchema = z.object({
    idSpot: z.string(),
    idOrder: z.string(),
    idWidget: z.string(),
    type: z.string(),
    title: z.string(),
    url: z.string(),
    visible: z.string(),
});
export type GroupCustomWidget = z.infer<typeof groupCustomWidgetSchema>;

export const groupPropertiesOptionsSchema = z.object({
    edit_access: z.array(valueLabelSchema.and(z.object({ description: z.string() }))),
    commentLevel: z.array(valueLabelSchema.and(z.object({ selected: z.boolean().optional() }))),
    displayType: z.array(valueLabelSchema),
    user_level_signup: z.array(valueLabelSchema.and(z.object({ selected: z.boolean().optional() }))),
    spot_right_user: z.array(valueLabelSchema.and(z.object({ selected: z.boolean().optional() }))),
    useTeaser: z.array(valueLabelSchema),
    spotDefaultPublishType: z.array(z.string()),
    mail_frequency: z.array(valueLabelSchema),
    widgets: z.array(groupWidgetLittleSchema),
    custom_widgets: z.record(groupCustomWidgetSchema).or(z.array(z.never())),
    isArchive: z.number(),
    fileSyncVersioningType: z.array(valueLabelSchema),
});
export type GroupPropertiesOptions = z.infer<typeof groupPropertiesOptionsSchema>;

export const rssTypeSchema = z.object({
    url: z.string(),
    idUser: z.number(),
    send: z.boolean().optional(),
    _user: jUserLittleSchema.optional(),
    date: z.string().optional(),
    error: z.string().optional(),
});
export type RssType = z.infer<typeof rssTypeSchema>;

export const groupPropertiesSchema = z.object({
    edit_access: z.union([z.literal(''), z.literal('author')]),
    commentLevel: z.union([z.literal('-1'), z.literal('1'), z.literal('2'), z.literal('3')]),
    user_level_signup: z.union([z.literal(''), z.literal('1'), z.literal('2'), z.literal('3')]),
    nounsubscribe: z.union([z.literal('0'), z.literal('1')]),
    useTeaser: z.union([z.literal(''), z.literal('0'), z.literal('1')]).nullable(),
    displayType: z.string(),
    spot_right_user: z.string(),
    rss_feed: z.array(rssTypeSchema).nullable(),
    spotDefaultPublishType: z.string(),
    mail_frequency: z.string(),
    mail_comment: z.union([z.literal('1'), z.literal('')]),
    mail_spotit: z.union([z.literal('1'), z.literal('')]),
    mail_subscriber: z.union([z.literal('1'), z.literal('')]),
    lock_mail: z.union([z.literal('1'), z.literal('')]),
    activity: z.union([z.literal('1'), z.literal('0')]),
    publishFolder: z.union([z.literal('1'), z.literal('0')]),
    notifyFolder: z.union([z.literal('1'), z.literal('0')]),
    widgets: z.array(z.string()),
    rss_access: z.union([z.literal('1'), z.literal('0')]),
    rss_key: z.string(),
    fileSyncEnabled: z.union([z.literal('1'), z.literal('0')]),
    fileBankQuota: z.string(),
    fileSyncVersioningType: z.union([z.literal('overwrite'), z.literal('author-change')]),
    _options: groupPropertiesOptionsSchema,
    _errors: z.tuple([]),
    lastRssExecution: z.record(z.string()).optional(),
});
export type GroupProperties = z.infer<typeof groupPropertiesSchema>;

export const groupWidgetsSchema = z.array(groupWidgetLittleSchema);
export type GroupWidgets = z.infer<typeof groupWidgetsSchema>;

export const updateGroupSchema = createGroupSchema.and(groupPropertiesSchema.omit({ rss_feed: true }).partial()).and(
    z.object({
        uri: z.string(),
        image: z.string().nullable().optional(),
        spotBanner: z.record(spotBannerImageSchema).or(z.array(z.never())).optional(),
        token: z.string().optional(),
        uriCommunity: z.string().optional(),
        custom_widgets: z.record(groupCustomWidgetSchema).optional(),
        rss_feed: z.string(),
    }),
);
export type UpdateGroup = z.infer<typeof updateGroupSchema>;

export const groupWidgetSchema = z.object({
    idWidget: z.string(),
    title: z.string(),
    url: z.string(),
    type: z.string(),
    icon: z.string(),
    description: z.string(),
});
export type GroupWidget = z.infer<typeof groupWidgetSchema>;

export const categorySchema = z.object({
    id: z.string(),
    idorder: z.number(),
    label: z.string(),
});
export type Category = z.infer<typeof categorySchema>;

export const jGroupLittleSchema = jObjectLittleSchema.extend({
    dateCreation: z.string(),
    dateModified: z.string(),
    category: z.number(),
    privacy: groupPrivacy,
});
export type jGroupLittle = z.infer<typeof jGroupLittleSchema>;

export const jGroupListSchema = jGroupLittleSchema.extend({
    description: z.string(),
    edito: z.string().nullable(),
    language: z.string(),
});
export type jGroupList = z.infer<typeof jGroupListSchema>;

export const jGroupViewSchema = jGroupListSchema.extend({
    image: z.string().nullable().optional(),
    spotBanner: z.record(spotBannerImageSchema).or(z.array(z.never())).optional(),
    idCommunity: z.string().optional(),
    useTeaser: z
        .union([z.literal(''), z.literal('0'), z.literal('1')])
        .nullable()
        .optional(),
});
export type jGroupView = z.infer<typeof jGroupViewSchema>;

export const resetNotificationConfigurationSchema = z.object({
    idSpot: z.union([z.string(), z.number()]),
    mailFrequency: z.union([z.string(), z.number()]),
    mailSpotit: z.boolean(),
    mailComment: z.boolean(),
    mailSubscriber: z.boolean(),
});
export type ResetNotificationConfiguration = z.infer<typeof resetNotificationConfigurationSchema>;

export const groupListConfigSchema = z.object({
    type: z.string().optional(),
    format: formatListSchema.optional(),
    query: z.string().optional(),
    limit: z.number().optional(),
    page: z.number().optional(),
    public: z.boolean().optional(),
    orders: ordersSchema.optional(),
    filters: filtersSchema.or(z.undefined()).optional(),
});
export type GroupListConfig = z.infer<typeof groupListConfigSchema>;

export const groupCategoriesConfigurationSchema = z.object({
    openCategory: z.boolean(),
});
export type GroupCategoriesConfiguration = z.infer<typeof groupCategoriesConfigurationSchema>;

// ZOD validation
export const validateIsFormatUserLevelSpot = (object: object): object is UserLevelSpot =>
    userLevelSpotSchema.safeParse(object).success;

export const groupMessengerDiscussionSchema = jObjectBaseSchema.extend({
    isMessengerSpotGroup: z.boolean(),
    disabled: z.boolean(),
});
export type GroupMessengerDiscussion = z.infer<typeof groupMessengerDiscussionSchema>;
